'use client'

import { Paginated } from '@/api'
import { CommentRow } from '../comment/CommentRow'
import { CommentInput } from '../comment/CommentInput'
import { WithT } from '@/locales/types'
import { EmptyCard } from '../EmptyCard'
import { useInfinitePagination } from './InfinitePagination'
import { createT } from '@/locales/index'
import { CommentStatus } from '@/api/types/comment'
import React from 'react'
import {
  CommentApis,
  IBaseCommentRO,
  IPaginateComment,
} from '@/api/interfaces/IComment'

interface PaginatedCommentProps<T extends IBaseCommentRO = IBaseCommentRO> {
  initial: Paginated<T>
  domainId: number
  params: IPaginateComment
  functions: CommentApis<T>
  showCount?: boolean
  emptyText?: string
  renderNicknameDecoration?: (item: T) => React.ReactNode
}

export const PaginatedComment = <T extends IBaseCommentRO = IBaseCommentRO>({
  initial,
  domainId,
  params,
  functions,
  showCount,
  translations,
  renderNicknameDecoration,
}: WithT<PaginatedCommentProps<T>>) => {
  const t = createT(translations)

  const { rows, count, listBottom, update, unshift, deleteRow, deleteComment } =
    useInfinitePagination<T>({
      id: 'paginated-comments',
      paginateFunc: (args) => functions.paginate(domainId, args),
      params,
      initial,
    })

  const onCreate = (comment: T) => {
    unshift(comment)
  }

  const onCreateChild = (comment: T, index: number) => {
    const copy = JSON.parse(JSON.stringify(rows[index]))

    if (!copy.children) {
      copy.children = []
    }

    copy.children.unshift(comment)
    copy.childCount = (copy.childCount || 0) + 1

    update(copy, index, 1)
  }

  const onUpdate = (comment: T, index: number, childIndex?: number) => {
    const copy = JSON.parse(JSON.stringify(rows[index]))

    if (typeof childIndex === 'undefined') {
      Object.assign(copy, comment)
      update(copy, index)
      return
    }

    copy.children![childIndex] = comment
    update(copy, index)
  }

  const onDelete = (index: number, childIndex?: number) => {
    const copy = JSON.parse(JSON.stringify(rows[index]))

    if (typeof childIndex === 'undefined') {
      const childCount = copy.childCount

      if (!childCount) {
        deleteRow(index)
      } else {
        deleteComment(index)
      }
    } else {
      copy.children!.splice(childIndex, 1)
      copy.childCount = (copy.childCount || 1) - 1
      update(copy, index, -1)

      if (
        rows[index].commentStatus === CommentStatus.DELETED &&
        !copy.childCount
      ) {
        deleteRow(index, true)
      }
    }
  }

  const onLoadMore = (index: number, children: T[]) => {
    const copy = JSON.parse(JSON.stringify(rows[index]))
    copy.children = children
    update(copy, index)
  }

  return (
    <div className="flex flex-col gap-4">
      {showCount && (
        <div className="flex gap-1">
          <span className="font-bold text-subtitle1">{t('comment')}</span>
          <span className="font-bold text-key text-subtitle1">{count}</span>
        </div>
      )}

      <CommentInput
        domainId={domainId}
        createFn={functions.create}
        translations={translations}
        onCreateOrUpdate={onCreate}
      />

      {!count ? (
        <EmptyCard
          translations={translations}
          type="NON-SEARCH"
          text={t('empty-list-comment')}
        />
      ) : (
        <ul className="flex flex-col">
          {rows.map((row, i) => (
            <CommentRow
              index={i}
              key={row.id}
              item={row}
              translations={translations}
              domainId={domainId}
              functions={functions}
              onUpdate={onUpdate}
              onCreateChild={onCreateChild}
              onLoadMore={onLoadMore}
              onDelete={onDelete}
              renderNicknameDecoration={() => renderNicknameDecoration?.(row)}
            />
          ))}
        </ul>
      )}
      <div ref={listBottom} />
    </div>
  )
}
